import React, { useState, useEffect } from 'react';
import Breadcrumb from '../Components/Breadcrumb';
import Pagehelmet from '../Components/Pagehelmet';
import { useNavigate } from 'react-router-dom';

const ProfilePage = () => {
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);

  // Initialize with empty data structure
  const [profileData, setProfileData] = useState({
    address: "",
    email: "",
    emergencyContact: "",
    familyMembers: "",
    firstName: "",
    lastName: "",
    phone: "",
    preferences: "",
    role: ""
  });

  // Fetch user data from localStorage on component mount
  useEffect(() => {
    const fetchUserData = () => {
      try {
        setLoading(true);
        const userData = localStorage.getItem('User_Info');
        
        if (userData) {
          const parsedData = JSON.parse(userData);
          setProfileData(parsedData);
        } else {
          setError("No user data found");
          // Redirect to login if no user data found
          setTimeout(() => navigate('/'), 2000);
        }
      } catch (err) {
        setError("Failed to load user data");
        console.error("Error loading user data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [navigate]);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      setLoading(true);
      
      // Save updated data to localStorage
      localStorage.setItem('User_Info', JSON.stringify(profileData));
      
      setSuccess(true);
      setEditMode(false);
      
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError("Failed to update profile");
      console.error("Update error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    // Clear all user-related data
    localStorage.removeItem('token');
    localStorage.removeItem('User_Info');
    localStorage.removeItem('email');
    
    // Redirect to login page
    navigate('/');
  };

  if (loading) {
    return (
      <div className="container text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Pagehelmet pageTitle="My Profile" />
      <Breadcrumb title="My Profile" />

      <section className="profile-section py-5">
        <div className="container">
          <div className="row">
            <div className="col-lg-4 mb-4">
              <div className="card p-4 text-center">
                <div className="avatar-placeholder rounded-circle bg-light d-flex align-items-center justify-content-center mx-auto mb-3" 
                     style={{width: '120px', height: '120px', fontSize: '48px'}}>
                  {profileData.firstName?.charAt(0) || ''}{profileData.lastName?.charAt(0) || ''}
                </div>
                <h3>{profileData.firstName} {profileData.lastName}</h3>
                <p className="text-muted text-capitalize">{profileData.role}</p>
                
                <div className="d-flex flex-column gap-2">
                  <button 
                    className={`btn ${editMode ? 'btn-outline-danger' : 'btn-outline-primary'}`}
                    onClick={() => setEditMode(!editMode)}
                  >
                    {editMode ? 'Cancel Editing' : 'Edit Profile'}
                  </button>
                  <button 
                    className="btn btn-outline-danger"
                    onClick={handleLogout}
                  >
                    Sign Out
                  </button>
                </div>
              </div>
            </div>

            <div className="col-lg-8">
              <div className="card p-4">
                {error && <div className="alert alert-danger">{error}</div>}
                {success && <div className="alert alert-success">Profile updated successfully!</div>}

                <form onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="firstName" className="form-label">First Name *</label>
                      <input
                        type="text"
                        id="firstName"
                        className="form-control"
                        value={profileData.firstName || ''}
                        onChange={handleChange}
                        disabled={!editMode}
                        required
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label htmlFor="lastName" className="form-label">Last Name *</label>
                      <input
                        type="text"
                        id="lastName"
                        className="form-control"
                        value={profileData.lastName || ''}
                        onChange={handleChange}
                        disabled={!editMode}
                        required
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label htmlFor="email" className="form-label">Email *</label>
                      <input
                        type="email"
                        id="email"
                        className="form-control"
                        value={profileData.email || ''}
                        onChange={handleChange}
                        disabled={!editMode}
                        required
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label htmlFor="phone" className="form-label">Phone</label>
                      <input
                        type="tel"
                        id="phone"
                        className="form-control"
                        value={profileData.phone || ''}
                        onChange={handleChange}
                        disabled={!editMode}
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label htmlFor="role" className="form-label">Role</label>
                      <select
                        id="role"
                        className="form-select"
                        value={profileData.role || 'parent'}
                        onChange={handleChange}
                        disabled={!editMode}
                      >
                        <option value="camper">Camper</option>
                        <option value="parent">Parent</option>
                      </select>
                    </div>

                    <div className="col-md-6 mb-3">
                      <label htmlFor="address" className="form-label">Address</label>
                      <input
                        type="text"
                        id="address"
                        className="form-control"
                        value={profileData.address || ''}
                        onChange={handleChange}
                        disabled={!editMode}
                      />
                    </div>

                    <div className="col-12 mb-3">
                      <label htmlFor="emergencyContact" className="form-label">Emergency Contact</label>
                      <input
                        type="text"
                        id="emergencyContact"
                        className="form-control"
                        value={profileData.emergencyContact || ''}
                        onChange={handleChange}
                        disabled={!editMode}
                      />
                    </div>

                    <div className="col-12 mb-3">
                      <label htmlFor="preferences" className="form-label">Preferences</label>
                      <input
                        type="text"
                        id="preferences"
                        className="form-control"
                        value={profileData.preferences || ''}
                        onChange={handleChange}
                        disabled={!editMode}
                      />
                    </div>

                    <div className="col-12 mb-3">
                      <label htmlFor="familyMembers" className="form-label">Family Members (comma separated)</label>
                      <input
                        type="text"
                        id="familyMembers"
                        className="form-control"
                        value={profileData.familyMembers || ''}
                        onChange={handleChange}
                        disabled={!editMode}
                        placeholder="John, Jane, etc."
                      />
                    </div>

                    {editMode && (
                      <div className="col-12 mt-4">
                        <button 
                          type="submit" 
                          className="btn btn-primary"
                          disabled={loading}
                        >
                          {loading ? 'Saving...' : 'Save Changes'}
                        </button>
                      </div>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProfilePage;